import { useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { getIDFromUrl } from '@rossum/api-client';
import { Note } from '@rossum/api-client/notes';
import { User } from '@rossum/api-client/users';
import {
  IconDotsVertical,
  IconPencil,
  IconSpy,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useCommentsUsers } from '../hooks/useCommentsUsers';
import { useDeleteNote } from '../hooks/useDeleteNote';
import { useUpdateComment } from '../hooks/useUpdateComment';
import { formatCommentDate, isCommentEdited } from '../utils';
import { CommentsChip } from './CommentsChip';
import { CommentTextarea } from './CommentTextarea';

const getAuthorName = (user: User) =>
  user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`.trim()
    : (user.username ?? user.email ?? '');

type CommentsThreadProps = {
  comments: Note[];
  hasWriteAcess: boolean;
};

type CommentCardProps = {
  comment: Note;
  usersMap: Record<string, User> | undefined;
  isLoading: boolean;
  hasWriteAcess: boolean;
};

const MENU_BUTTON_CLASSNAME = 'comments-action-menu';

const CommentCard = ({
  comment,
  usersMap,
  isLoading,
  hasWriteAcess,
}: CommentCardProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [contentToEdit, setContentToEdit] = useState<string | null>(null);

  const { mutate: deleteNote, isLoading: isDeleting } = useDeleteNote();
  const { mutate: updateComment, isLoading: isUpdating } = useUpdateComment();

  const formattedCreateDate = formatCommentDate(comment.createdAt, intl);
  const formattedModifiedDate = comment.modifiedAt
    ? formatCommentDate(comment.modifiedAt, intl)
    : null;

  const author = usersMap?.[comment.creator];
  const { firstName: modifierFirstName, lastName: modifierLastName } =
    comment.modifier && usersMap ? (usersMap[comment.modifier] ?? {}) : {};

  const handleDelete = () => {
    setAnchorEl(null);
    deleteNote(comment.id);
  };

  const handleEditClick = () => {
    setContentToEdit(comment.content);
    setAnchorEl(null);
  };

  const handleEditSubmit = () => {
    if (!contentToEdit) return;
    updateComment(
      { id: comment.id, payload: { content: contentToEdit } },
      { onSuccess: () => setContentToEdit(null) }
    );
  };

  const userInitials =
    author && author.firstName && author.lastName
      ? R.pipe([author.firstName, author.lastName], R.map(R.sliceString(0, 1)))
      : null;

  const isEdited = comment.modifiedAt
    ? isCommentEdited({
        createdAt: comment.createdAt,
        modifiedAt: comment.modifiedAt,
      })
    : false;

  const isEditing = contentToEdit !== null;
  return (
    <Stack
      sx={{
        px: 2,
        '&:hover': {
          [`& .${MENU_BUTTON_CLASSNAME}`]: {
            visibility: 'visible',
          },
        },
        ...(contentToEdit !== null
          ? {
              backgroundColor: t => t.palette.background.default,
              border: t => `1px solid ${t.palette.divider}`,
              borderRadius: borderRadius('m'),
              py: 2,
            }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap>
        {isLoading ? (
          <Skeleton variant="circular" width={32} height={32} />
        ) : (
          <Avatar
            sx={{
              padding: 1,
              width: 32,
              height: 32,
            }}
            variant="outlined"
          >
            {userInitials ? (
              <Typography variant="body2">{userInitials}</Typography>
            ) : (
              <SvgIcon fontSize="small">
                <IconSpy />
              </SvgIcon>
            )}
          </Avatar>
        )}

        <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={1} useFlexGap>
          <Stack>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              spacing={1}
              useFlexGap
            >
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width={150} />
                ) : author ? (
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                  >
                    {getAuthorName(author)}
                  </Typography>
                ) : null}
                <Tooltip
                  title={
                    formattedCreateDate
                      ? `${formattedCreateDate.date} - ${formattedCreateDate.time}`
                      : ''
                  }
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    whiteSpace="nowrap"
                  >
                    {formattedCreateDate ? formattedCreateDate.date : '-'}
                  </Typography>
                </Tooltip>
              </Stack>
              {!isEditing && hasWriteAcess ? (
                <IconButton
                  className={MENU_BUTTON_CLASSNAME}
                  size="small"
                  onClick={e => setAnchorEl(e.currentTarget)}
                  disabled={isDeleting}
                  sx={{ visibility: 'hidden' }}
                >
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SvgIcon fontSize="small">
                      <IconDotsVertical />
                    </SvgIcon>
                  )}
                </IconButton>
              ) : null}
            </Stack>
            <Stack>
              {isEditing ? (
                <Stack spacing={1} useFlexGap sx={{ pt: 1 }}>
                  <CommentTextarea
                    value={contentToEdit}
                    onChange={e => setContentToEdit(e.target.value)}
                    autoFocus
                    disabled={isUpdating}
                    onSubmit={handleEditSubmit}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setContentToEdit(null)}
                    >
                      {intl.formatMessage({
                        id: 'components.documentComments.actions.editCancel',
                      })}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleEditSubmit}
                      disabled={isUpdating}
                      startIcon={
                        isUpdating ? <CircularProgress size={16} /> : null
                      }
                    >
                      {intl.formatMessage({
                        id: 'components.documentComments.actions.editSave',
                      })}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {comment.content}
                </Typography>
              )}
              {!isEditing && isEdited && formattedModifiedDate ? (
                <Tooltip
                  title={`${formattedModifiedDate.date} - ${formattedModifiedDate.time}`}
                >
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ mt: 0.5 }}
                  >
                    {modifierFirstName && modifierLastName
                      ? intl.formatMessage(
                          { id: 'components.documentComments.editedBy' },
                          {
                            firstName: modifierFirstName,
                            lastName: modifierLastName,
                            date: formattedModifiedDate.date,
                          }
                        )
                      : intl.formatMessage(
                          { id: 'components.documentComments.editedByUnknown' },
                          {
                            date: formattedModifiedDate.date,
                          }
                        )}
                  </Typography>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
          <CommentsChip commentType={comment.type} />
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{ list: { dense: true } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ListItem disablePadding>
          <ListItemButton onClick={handleEditClick}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SvgIcon>
                <IconPencil />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({
                id: 'components.documentComments.actions.edit',
              })}
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleDelete} disabled={isDeleting}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SvgIcon>
                <IconTrash />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({
                id: 'components.documentComments.actions.delete',
              })}
            </ListItemText>
          </ListItemButton>
        </ListItem>
      </Menu>
    </Stack>
  );
};

export const CommentsThread = ({
  comments,
  hasWriteAcess,
}: CommentsThreadProps) => {
  const userIds = comments
    ? R.unique(comments.map(comment => getIDFromUrl(comment.creator)))
    : [];

  const { data: usersMap, isLoading } = useCommentsUsers(userIds);

  return (
    <Stack spacing={3} useFlexGap sx={{ px: 1 }}>
      {comments.map(c => (
        <CommentCard
          key={c.id}
          comment={c}
          usersMap={usersMap}
          isLoading={isLoading}
          hasWriteAcess={hasWriteAcess}
        />
      ))}
    </Stack>
  );
};
